import { call, put, takeLatest, select } from 'redux-saga/effects'
import { trackInternalEvent } from '@rio/tracking'
import { Resume } from 'packages/types'
import { ConversationalBuilderState, AiChange } from 'builder/features/conversationalBuilder/types'
import {
  selectors as resumeEditorSelectors,
  actions as resumeEditorActions,
} from 'builder/modules/resumeEditor'
import { ApiResponse, apiClient } from '../apiClient'
import { actions, selectors } from './conversationalBuilderModule'

import { ERROR_MESSAGE } from './constants'

interface AiResponse {
  assistantMessage: string
  conversation: object[]
  resume: Resume
  edited?: boolean
  changes?: AiChange[]
}

export function* handleSendMessage(
  action: ReturnType<typeof actions.sendMessage>,
): Generator<object, void, unknown> {
  try {
    const resumeData = (yield select(resumeEditorSelectors.resume)) as ReturnType<
      typeof resumeEditorSelectors.resume
    >

    yield put(actions.clearPendingChanges())

    yield put(actions.addUserMessage(action.payload))

    const state = (yield select(selectors.messages)) as ConversationalBuilderState['messages']

    trackInternalEvent('chat_send_question', {
      question: action.payload,
      label: 'builder',
    })

    const { data } = (yield call(apiClient.post, '/ai-copilot/chat', {
      conversation: state,
      resume: resumeData,
      options: { locale: 'en', debug: true, targetRole: resumeData?.jobTitle },
    })) as ApiResponse<AiResponse>

    if (!data) throw new Error('Error sending message')

    yield put(actions.receiveMessage(data))

    trackInternalEvent('chat_answer_received', {
      answer: data.assistantMessage,
      label: 'builder',
    })
  } catch (error) {
    yield put(actions.setError(ERROR_MESSAGE))
  }
}

export function* handleRefreshMessage(): Generator<object, void, unknown> {
  try {
    const resumeData = (yield select(resumeEditorSelectors.resume)) as ReturnType<
      typeof resumeEditorSelectors.resume
    >

    const state = (yield select(selectors.messages)) as ConversationalBuilderState['messages']

    const { data } = (yield call(apiClient.post, '/ai-copilot/chat', {
      conversation: state,
      resume: resumeData,
      options: { locale: 'en', debug: true, targetRole: resumeData?.jobTitle },
    })) as ApiResponse<AiResponse>

    if (!data) throw new Error('Error refreshing message')

    yield put(actions.receiveMessage(data))

    trackInternalEvent('chat_answer_received', {
      answer: data.assistantMessage,
      label: 'builder',
    })
  } catch (error) {
    yield put(actions.setError(ERROR_MESSAGE))
  }
}

export function* handleApplyPendingChanges(): Generator<object, void, unknown> {
  try {
    const pendingChanges = (yield select(selectors.pendingChanges)) as AiChange[]

    if (pendingChanges && pendingChanges.length > 0) {
      for (const change of pendingChanges) {
        yield put(
          resumeEditorActions.updateSimpleField({
            name: change.path,
            value: change.after,
            debounce: false,
          } as any),
        )
      }

      yield put(actions.clearPendingChanges())
    }
  } catch (error) {
    yield put(actions.setError(ERROR_MESSAGE))
  }
}

export function* sagas() {
  yield takeLatest(actions.sendMessage.type, handleSendMessage)
  yield takeLatest(actions.refreshLastMessage.type, handleRefreshMessage)
  yield takeLatest(actions.applyPendingChanges.type, handleApplyPendingChanges)
}
